import { useCallback, useRef } from 'react';

import useGoogleGeolocationLibrary from 'hooks/geolocation/use-google-geolocation-library';
import { IPlaceData, parsePlaceAddress } from 'utils/geolocation';

type GoogleGeocode = google.maps.Geocoder;
type GeocoderResult = google.maps.GeocoderResult;

export default function usePlaceIdDetails() {
  const { libraryLoaded } = useGoogleGeolocationLibrary();
  const geocoder = useRef<GoogleGeocode>(undefined);

  const getDetails = useCallback(
    (placeId?: string): Promise<IPlaceData | undefined> =>
      new Promise(resolve => {
        if (!placeId || !libraryLoaded) {
          resolve(undefined);
        } else {
          if (!geocoder.current) {
            geocoder.current = new window.google.maps.Geocoder();
          }

          geocoder.current.geocode({ placeId }, (results, status) => {
            if (status !== window?.google?.maps.GeocoderStatus.OK) {
              return resolve(undefined);
            }

            const firstResult: GeocoderResult | null = results?.length ? results[0] : null;
            if (!firstResult) {
              return resolve(undefined);
            }

            const { geometry, address_components } = firstResult;
            const placeData = {
              coordinates: {
                lat: geometry!.location.lat(),
                lng: geometry!.location.lng(),
              },
              address: parsePlaceAddress(address_components, {
                useISOCountry: true,
              }),
            };
            resolve(placeData);
          });
        }
      }),
    [libraryLoaded]
  );

  return getDetails;
}
